import React, { useEffect, useState } from 'react';
import { toast } from '../../../../ui/toast';
import { Button } from '../../../../ui';
import LoadingSpinner from '../../../job_openings/create/ui/LoaderSpinner';
import { getPermissions } from '../../../../utils/helpers/auth';
import { getAuthSettings, putAuthSettings, testSso } from '../../../../utils/api_service/mfa_api';

const POLICIES: { value: string; label: string; help: string }[] = [
  { value: 'off', label: 'Off', help: 'Members sign in with a password only.' },
  { value: 'required_for_admins', label: 'Required for admins', help: 'Admins must use an authenticator app.' },
  { value: 'required_for_all', label: 'Required for everyone', help: 'All members (not candidates) must use MFA.' },
];

export const SecuritySettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordLoginEnabled, setPasswordLoginEnabled] = useState(true);
  const [mfaPolicy, setMfaPolicy] = useState('off');
  const [sso, setSso] = useState<any>(null);
  const [ssoForm, setSsoForm] = useState<any>({ provider: 'google', issuer: '', clientId: '', clientSecret: '', allowedEmailDomains: '', ssoSatisfiesMfa: false, enabled: false });
  const [redirectUri, setRedirectUri] = useState('');

  const token = localStorage.getItem('token');
  const canEdit = token ? getPermissions(token).includes('admin:security') : false;

  useEffect(() => {
    getAuthSettings()
      .then((r) => r.json())
      .then((d) => {
        if (d && typeof d.mfaPolicy === 'string') {
          setMfaPolicy(d.mfaPolicy);
          setPasswordLoginEnabled(Boolean(d.passwordLoginEnabled));
          if (d.sso) {
            setSso(d.sso);
            setRedirectUri(d.sso.redirectUri || '');
            setSsoForm({
              provider: d.sso.provider || 'google', issuer: d.sso.issuer || '', clientId: d.sso.clientId || '',
              clientSecret: '', allowedEmailDomains: (d.sso.allowedEmailDomains || []).join(', '),
              ssoSatisfiesMfa: d.sso.ssoSatisfiesMfa === true, enabled: !!d.sso.enabled,
            });
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const buildSso = () => ({
    provider: ssoForm.provider,
    issuer: ssoForm.issuer.trim(),
    clientId: ssoForm.clientId.trim(),
    ...(ssoForm.clientSecret ? { clientSecret: ssoForm.clientSecret } : {}),
    allowedEmailDomains: ssoForm.allowedEmailDomains.split(',').map((x: string) => x.trim()).filter(Boolean),
    ssoSatisfiesMfa: ssoForm.ssoSatisfiesMfa,
    enabled: ssoForm.enabled,
  });

  const saveSso = async (enable: boolean) => {
    const body: any = { passwordLoginEnabled, mfaPolicy, sso: { ...buildSso(), enabled: enable } };
    const res = await putAuthSettings(body);
    const d = await res.json().catch(() => ({}));
    if (res.ok) { toast.success('SSO settings saved', { position: 'bottom-center' }); setSso(d.sso); setSsoForm((f: any) => ({ ...f, enabled: !!d.sso?.enabled })); }
    else { toast.error(d.error || 'Could not save SSO. Test the connection before enabling.', { position: 'bottom-center' }); }
  };

  const runTest = async () => {
    // Persist config first (disabled), then test discovery.
    const saved = await putAuthSettings({ passwordLoginEnabled, mfaPolicy, sso: { ...buildSso(), enabled: false } });
    if (saved.ok) {
      const sd = await saved.json().catch(() => ({}));
      setSso(sd.sso || null);
      setSsoForm((f: any) => ({ ...f, enabled: false })); // SSO stays off until enabled after a passing test
    }
    const res = await testSso();
    const d = await res.json().catch(() => ({}));
    if (d.ok) toast.success('Connection verified — you can enable SSO now', { position: 'bottom-center' });
    else toast.error('Test failed: ' + (d.problems ? d.problems.join(', ') : d.error || 'unreachable'), { position: 'bottom-center' });
  };

  const removeSso = async () => {
    await putAuthSettings({ passwordLoginEnabled, mfaPolicy, removeSso: true });
    setSso(null); setSsoForm({ provider: 'google', issuer: '', clientId: '', clientSecret: '', allowedEmailDomains: '', ssoSatisfiesMfa: false, enabled: false });
    toast.success('SSO removed', { position: 'bottom-center' });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await putAuthSettings({ passwordLoginEnabled, mfaPolicy });
      if (res.ok) {
        toast.success('Security settings saved', { position: 'bottom-center' });
      } else if (res.status === 401 || res.status === 403) {
        toast.error('You do not have permission to change security settings.', { position: 'bottom-center' });
      } else {
        toast.error('Could not save security settings.', { position: 'bottom-center' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className='flex flex-col justify-center items-center min-h-[350px] gap-3'>
        <LoadingSpinner label='' />
        <p className='text-xs font-semibold text-slate-400 tracking-wide uppercase'>
          Loading Security Settings...
        </p>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-6 max-w-2xl p-1'>
      <div>
        <h3 className='text-lg font-semibold text-slate-900'>Multi-factor authentication</h3>
        <p className='text-sm text-slate-500'>
          Candidates and one-off assessment / interview links are never affected by this policy.
        </p>
      </div>

      <fieldset className='flex flex-col gap-3' disabled={!canEdit}>
        {POLICIES.map((p) => (
          <label key={p.value} className='flex items-start gap-3 rounded-lg border border-slate-200 p-3 cursor-pointer'>
            <input type='radio' name='mfaPolicy' value={p.value} checked={mfaPolicy === p.value}
                   onChange={() => setMfaPolicy(p.value)} className='mt-1' />
            <span>
              <span className='font-medium text-slate-800'>{p.label}</span>
              <span className='block text-xs text-slate-500'>{p.help}</span>
            </span>
          </label>
        ))}
      </fieldset>

      <div>
        <h3 className='text-lg font-semibold text-slate-900'>Password sign-in</h3>
        <label className='flex items-center gap-3 mt-2'>
          <input type='checkbox' checked={passwordLoginEnabled} disabled={!canEdit}
                 onChange={(e) => setPasswordLoginEnabled(e.target.checked)} />
          <span className='text-sm text-slate-700'>Allow members to sign in with a password</span>
        </label>
      </div>

      {canEdit && (
        <div className='border-t border-slate-200 pt-6'>
          <h3 className='text-lg font-semibold text-slate-900'>Single sign-on (SSO)</h3>
          <p className='text-sm text-slate-500 mb-3'>OpenID Connect. Register the redirect URI below in your identity provider.</p>
          {redirectUri && (
            <div className='mb-3'>
              <label className='text-xs text-slate-500'>Redirect URI</label>
              <div className='rounded bg-slate-100 p-2 font-mono text-xs break-all'>{redirectUri}</div>
            </div>
          )}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
            <label className='text-sm'>Provider
              <select className='mt-1 w-full border rounded p-2' value={ssoForm.provider}
                      onChange={(e) => setSsoForm({ ...ssoForm, provider: e.target.value })}>
                <option value='google'>Google Workspace</option>
                <option value='microsoft'>Microsoft Entra</option>
                <option value='okta'>Okta</option>
                <option value='generic'>Generic OIDC</option>
              </select>
            </label>
            <label className='text-sm'>Issuer URL
              <input className='mt-1 w-full border rounded p-2' value={ssoForm.issuer}
                     onChange={(e) => setSsoForm({ ...ssoForm, issuer: e.target.value })} placeholder='https://accounts.google.com' />
            </label>
            <label className='text-sm'>Client ID
              <input className='mt-1 w-full border rounded p-2' value={ssoForm.clientId}
                     onChange={(e) => setSsoForm({ ...ssoForm, clientId: e.target.value })} />
            </label>
            <label className='text-sm'>Client secret
              <input className='mt-1 w-full border rounded p-2' type='password' value={ssoForm.clientSecret}
                     onChange={(e) => setSsoForm({ ...ssoForm, clientSecret: e.target.value })}
                     placeholder={sso?.clientSecret || 'Enter to set/replace'} />
            </label>
            <label className='text-sm sm:col-span-2'>Allowed email domains (comma-separated)
              <input className='mt-1 w-full border rounded p-2' value={ssoForm.allowedEmailDomains}
                     onChange={(e) => setSsoForm({ ...ssoForm, allowedEmailDomains: e.target.value })} placeholder='acme.com, acme.co' />
            </label>
          </div>
          <label className='flex items-center gap-2 mt-3 text-sm'>
            <input type='checkbox' checked={ssoForm.ssoSatisfiesMfa}
                   onChange={(e) => setSsoForm({ ...ssoForm, ssoSatisfiesMfa: e.target.checked })} />
            <span>SSO counts as multi-factor (the IdP enforces it)</span>
          </label>
          <div className='flex flex-wrap gap-2 mt-4 items-center'>
            <Button variant='secondary' onClick={runTest} label='Test connection' />
            {ssoForm.enabled ? (
              <Button onClick={() => saveSso(false)} label='Disable SSO' />
            ) : (
              <Button onClick={() => saveSso(true)} label='Save and enable SSO' />
            )}
            <button type='button' onClick={() => saveSso(ssoForm.enabled)} className='text-sm text-slate-500 underline'>Save without changing status</button>
            {sso && <button type='button' onClick={removeSso} className='text-sm text-red-600 underline'>Remove SSO</button>}
          </div>
        </div>
      )}

      {canEdit ? (
        <div><Button onClick={save} disabled={saving} label={saving ? 'Saving…' : 'Save changes'} /></div>
      ) : (
        <p className='text-xs text-amber-700 bg-amber-50 rounded p-3'>
          These settings are read-only for you. Only members with the <b>Security</b> permission can change them.
          Ask an administrator to grant it from Roles.
        </p>
      )}
    </div>
  );
};
